import { registerTranslateConfig, get } from 'lit-translate';

export { translate, translateUnsafeHTML, get, use } from 'lit-translate';

/**
 *
 * @param value
 * @returns {boolean}
 */
const isObject = value => !!(value && typeof value === 'object' && value.constructor === Object);

/**
 * Translator usage examples:
 *    translate("key")
 *      -> Return simple value from dictionary.
 *
 *    translate("key", {REPLACE_KEY: 'new value'})
 *      -> Return value of 'key' with replaced [REPLACE_KEY] value as 'new value'.
 *
 *    translate("key", {REPLACE_KEY: {translateKey: 'key2', value: 1}})
 *      -> Return value of 'key' with replaced [REPLACE_KEY] value.
 *      -> New [REPLACE_KEY] value is output of function key2(value) from dictionary.
 *
 * !IMPORTANT!
 *    Is necessary to use brackets to define string for replace.
 *    Example:
 *    'This is [TEST] [TEXT]' -> There are possible to replace TEST and TEXT.
 *
 *
 * Initialize translator.
 * localeItems example: {'sk-SK': {data1: 'translateData1', data2: 'translateData2'}}
 *
 *
 * @param {Object} localeItems - configuration of different locales
 * @param {Object} localeItems.<locale> - key-value translation data for given locale
 * @param {string} localeItems.<locale>.<data> - translation of <data>
 */
export function initializeTranslator(localeItems) {
  registerTranslateConfig({
    /**
     * Localization file registration.
     *
     * @param {Object} lang
     * @returns {Promise<any>}
     */
    loader: lang =>
      new Promise(resolve => {
        resolve(localeItems[lang]);
      }),

    /**
     * Value replacer.
     *
     * Apply replace functionality over string.
     * Return final string value from js dictionary.
     *
     * @param {string | function} dictionaryEntry - value from js dictionary
     * @param {object} replacement - defined replacements
     * @returns {string}
     */
    interpolate: (dictionaryEntry, replacement) => {
      if (typeof dictionaryEntry === 'function') {
        return dictionaryEntry(replacement.value);
      }

      let interpolatedText = dictionaryEntry;

      for (const [key, value] of Object.entries(replacement)) {
        let newValue;
        if (isObject(value)) {
          newValue = get(value.translateKey);
          if (typeof newValue === 'function') {
            newValue = newValue(value.value);
          }
        } else {
          newValue = String(value);
        }

        interpolatedText = interpolatedText.replace(`[${key}]`, newValue);
      }

      return interpolatedText;
    },

    /**
     * Value getter.
     *
     * Find translate value by 'key' from dictionary.
     * Return found string or function.
     *
     * @param {string} key - json string of translateValue
     * @param {object} config
     * @returns {string|function}
     */
    lookup: (key, config) => {
      const parts = key.split('.');
      let string = config.strings;

      while (string !== undefined && parts.length) {
        string = string[parts[0]];
        parts.shift();
      }

      if (typeof string === 'function') {
        return string;
      }
      return string !== undefined ? string.toString() : null;
    },
  });
}
